/**
 * @fileoverview Provides utility functions for computing string similarity using methods like Jaccard similarity and Levenshtein distance. Includes functions to order documents by similarity and find the most similar lines in a set of files, useful for text comparison and document analysis tasks.
 */

export type LineRangeDocument = {
  text: string
  start: number
  end: number
}

export type SimilarDocument = LineRangeDocument & {
  path: string
  similarity: number
}

/**
 * Compute character-level jaccard similarity (IoU) between two strings.
 * Returns a value between 0 and 1.
 */
export function characterLevelJaccardSimilarity(a: string, b: string) {
  a = a.trim()
  b = b.trim()
  const aChars = Array.from(a)
  const bChars = Array.from(b)
  const intersection = aChars.filter((char) => bChars.includes(char))
  const union = [...aChars, ...bChars].filter((char, index, self) => self.indexOf(char) === index)
  return intersection.length / union.length
}

/**
 * Compute Levenshtein similarity between two strings.
 * Returns a value between 0 and 1.
 */
export function levenshteinSimilarity(a: string, b: string) {
  a = a.trim()
  b = b.trim()
  // Calculate Levenshtein distance using dynamic programming
  const m = a.length
  const n = b.length
  const d: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0))

  // Initialize first row and column
  for (let i = 0; i <= m; i++) d[i]![0] = i
  for (let j = 0; j <= n; j++) d[0]![j] = j

  // Fill in the rest of the matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        d[i]![j] = d[i - 1]![j - 1]!
      } else {
        d[i]![j] = Math.min(
          d[i - 1]![j]! + 1, // deletion
          d[i]![j - 1]! + 1, // insertion
          d[i - 1]![j - 1]! + 1, // substitution
        )
      }
    }
  }

  // Normalize by dividing by max length of the two strings
  const distance = d[m]![n]! / Math.max(m, n)
  // Convert distance to similarity score between 0 and 1
  return 1 - distance
}

/**
 * Order documents by jaccard similarity to a given text.
 */
export function orderBySimilarity(
  text: string,
  documents: { text: string; path: string; start: number; end: number }[],
  similarityFunction: (a: string, b: string) => number,
): SimilarDocument[] {
  return documents
    .map((d) => ({
      ...d,
      similarity: similarityFunction(text, d.text),
    }))
    .sort((a, b) => b.similarity - a.similarity)
}

/**
 * Find the most similar lines in a set of files to a given text.
 *
 * 1-indexed
 */
export function mostSimilarLines(
  text: string,
  files: { path: string; content: string }[],
  similarityFunction: (a: string, b: string) => number = characterLevelJaccardSimilarity,
): SimilarDocument[] {
  const linesDocuments = files.flatMap((f) => {
    return f.content
      .split('\n')
      .map((line, index) => ({
        path: f.path,
        text: line,
        start: index + 1,
        end: index + 1,
      }))
      .filter((line) => line.text.trim() !== '')
  })
  const filteredDocuments = linesDocuments.filter((doc) => doc.text.trim() !== text.trim())
  return orderBySimilarity(text, filteredDocuments, similarityFunction)
}
